import { ReactNode } from 'react'
import { useFormContext } from 'react-hook-form'
import useSWR from 'swr'

import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { apiFetcher } from '@/data/fetcher'
import { CountrySelect } from '@/forms/components/CountrySelect'
import { UnifiedBusinessSearch } from '@/forms/components/UnifiedBusinessSearch'
import { components } from '@invo.space/shared'

import type { ParticipantFormValues } from './StandaloneParticipantForm'

type InvoicingBankAccount = components['schemas']['InvoicingBankAccount']

export interface ParticipantFormFullProps {
  prefix: 'customer' | 'beneficiary' | 'supplier'
  showAccountNumber?: boolean
  showDefaultBankAccount?: boolean
}

/** Sekce formuláře: nadpis a vysvětlení vlevo nahoře, pole pod tím. */
export const FormGroup = ({ title, description, children }: { title: string; description?: ReactNode; children: ReactNode }) => (
  <section className="grid gap-4 border-t pt-6 first:border-t-0 first:pt-0">
    <div className="space-y-1">
      <h2 className="text-base font-medium tracking-[-0.01em]">{title}</h2>
      {description && <p className="text-sm text-muted-foreground text-pretty">{description}</p>}
    </div>
    {children}
  </section>
)

const Field = ({
  label,
  htmlFor,
  error,
  hint,
  className,
  children,
}: {
  label: string
  htmlFor?: string
  error?: string
  hint?: string
  className?: string
  children: ReactNode
}) => (
  <div className={className}>
    <Label htmlFor={htmlFor} className="mb-1.5 text-[13px] font-normal text-muted-foreground">
      {label}
    </Label>
    {children}
    {error ? (
      <p className="mt-1.5 text-xs text-danger">{error}</p>
    ) : hint ? (
      <p className="mt-1.5 text-xs text-muted-foreground">{hint}</p>
    ) : null}
  </div>
)

/** Plný formulář kontaktu se seskupenými poli (založení a úprava kontaktu, dodavatel v nastavení). */
export const ParticipantFormFull = ({ prefix, showAccountNumber = false, showDefaultBankAccount = false }: ParticipantFormFullProps) => {
  const methods = useFormContext<ParticipantFormValues>()
  const {
    register,
    control,
    watch,
    setValue,
    formState: { errors },
  } = methods
  const e = errors[prefix]
  const id = (name: string) => `${prefix}-${name}`

  const { data: bankAccountsData } = useSWR<{ data?: InvoicingBankAccount[] }>(
    showDefaultBankAccount ? '/api/v2/bank-accounts' : null,
    apiFetcher,
  )
  const bankAccounts = bankAccountsData?.data ?? []
  const defaultBankAccountId = watch(`${prefix}.defaultBankAccountId`)

  return (
    <div className="space-y-8">
      <FormGroup title="Firma" description="Začněte psát název nebo IČO. Údaje doplníme z obchodního rejstříku.">
        <Field label="Název nebo IČO" error={e?.name ? 'Vyplňte název kontaktu.' : undefined}>
          <UnifiedBusinessSearch prefix={prefix} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="IČO" htmlFor={id('ico')}>
            <Input id={id('ico')} inputMode="numeric" className="tabular" {...register(`${prefix}.businessId`)} />
          </Field>
          <Field label="DIČ" htmlFor={id('dic')}>
            <Input id={id('dic')} className="tabular" {...register(`${prefix}.taxId`)} />
          </Field>
        </div>
      </FormGroup>

      <FormGroup title="Kontakt" description="Na e-mail posíláme faktury a upomínky.">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="E-mail" htmlFor={id('email')} error={e?.email ? 'E-mail se nezdá být platný.' : undefined}>
            <Input id={id('email')} type="email" autoComplete="email" aria-invalid={!!e?.email} {...register(`${prefix}.email`)} />
          </Field>
          <Field label="Telefon" htmlFor={id('phone')} error={e?.phone ? 'Telefon se nezdá být platný.' : undefined}>
            <Input id={id('phone')} type="tel" autoComplete="tel" aria-invalid={!!e?.phone} {...register(`${prefix}.phone`)} />
          </Field>
        </div>
      </FormGroup>

      <FormGroup title="Adresa">
        <Field label="Ulice a číslo popisné" htmlFor={id('street')}>
          <Input id={id('street')} autoComplete="street-address" {...register(`${prefix}.address.address`)} />
        </Field>
        <div className="grid gap-4 sm:grid-cols-[8rem_minmax(0,1fr)]">
          <Field label="PSČ" htmlFor={id('zip')}>
            <Input id={id('zip')} autoComplete="postal-code" className="tabular" {...register(`${prefix}.address.zip`)} />
          </Field>
          <Field label="Město" htmlFor={id('city')}>
            <Input id={id('city')} autoComplete="address-level2" {...register(`${prefix}.address.city`)} />
          </Field>
        </div>
        <Field label="Země" className="[&_[data-slot=select-trigger]]:w-full">
          <CountrySelect control={control} prefix={prefix} />
        </Field>
      </FormGroup>

      {(showAccountNumber || (showDefaultBankAccount && bankAccounts.length > 0)) && (
        <FormGroup title="Platební údaje">
          {showAccountNumber && (
            <div className="grid gap-4 sm:grid-cols-2">
              <Field label="Číslo účtu" htmlFor={id('account')} hint="Ve tvaru 123456789/0100">
                <Input id={id('account')} className="tabular" {...register(`${prefix}.accountNumber`)} />
              </Field>
              <Field label="IBAN" htmlFor={id('iban')}>
                <Input id={id('iban')} className="tabular" {...register(`${prefix}.iban`)} />
              </Field>
            </div>
          )}
          {showDefaultBankAccount && bankAccounts.length > 0 && (
            <Field label="Výchozí účet na fakturách" hint="Tento váš účet se předvyplní na nových fakturách pro kontakt.">
              <Select
                value={defaultBankAccountId ?? ''}
                onValueChange={(value) =>
                  setValue(`${prefix}.defaultBankAccountId`, value === '__none' ? null : value, { shouldDirty: true })
                }
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Podle nastavení účtu" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none">Podle nastavení účtu</SelectItem>
                  {bankAccounts.map((account) => (
                    <SelectItem key={account.id} value={account.id!}>
                      {account.name}
                      {account.accountNumber
                        ? ` (${account.accountNumber}/${account.bankCode ?? ''})`
                        : account.iban
                          ? ` (${account.iban})`
                          : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}
        </FormGroup>
      )}
    </div>
  )
}
